import React from "react";

import { Alert, AlertColor, Button, Snackbar } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import { ReactComponent as LoadingIcon } from "@assets/svg/loading-spinner.svg";

import { ActionButton, setMessage } from "../redux/notification";

// Define the RootState type to properly type the useSelector hook
interface RootState {
  notification: {
    message: {
      content: string;
      type: AlertColor;
      isLoading?: boolean;
      action?: ActionButton;
    } | null;
  };
}

export const Toast = () => {
  const dispatch = useDispatch();
  const message = useSelector((state: RootState) => state.notification.message);

  const handleClose = (event: React.SyntheticEvent | Event, reason?: string) => {
    // Prevent closing on clickaway when loading
    if (message?.isLoading && reason === "clickaway") {
      return;
    }
    dispatch(setMessage(null));
  };

  const handleActionClick = () => {
    if (message?.action?.onClick) {
      message.action.onClick();
      dispatch(setMessage(null)); // Close toast after action
    }
  };

  // Use the explicit isLoading flag from the message object
  const isLoading = message?.isLoading || false;

  return (
    <Snackbar
      open={Boolean(message)}
      autoHideDuration={isLoading ? null : message?.action ? 10000 : 6000} // 10s with action, 6s default
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
    >
      <Alert
        onClose={isLoading ? undefined : handleClose} // Don't allow closing loading toasts
        severity={message?.type || "info"}
        sx={{ width: "100%" }}
        icon={
          isLoading ? <LoadingIcon className="size-5 animate-spin-ccw" /> : undefined
        }
        action={
          message?.action ? (
            <Button
              color="inherit"
              size="small"
              onClick={handleActionClick}
              sx={{
                fontWeight: 600,
                textTransform: "uppercase",
                fontSize: "0.875rem",
              }}
            >
              {message.action.label}
            </Button>
          ) : undefined
        }
      >
        {message?.content || ""}
      </Alert>
    </Snackbar>
  );
};
